/**
 * P1-ENGINE D4 — two anchor statements with human confirmation + version history.
 *
 * Binding rules (P1 card deliverable 4 + SKAFER-STRATEGY-PLAN §2/§7):
 * - Exactly two anchors per design: designMissionStatement + skierDescription
 *   (schemas.ts AnchorsSection: ≤2000 chars each).
 * - The ENGINE drafts them from the interview payload; a HUMAN confirms or
 *   edits. Never auto-accept: an unconfirmed draft is a draft.
 * - Every revision is versioned — history is append-only, current state is
 *   derived (Decision 002 Q4 semantics). Editing never destroys the original.
 * - Deterministic drafting: the draft is a pure function of the payload.
 */
import type { AnchorsSection } from './schemas';

export const ANCHOR_RULES_VERSION = '0.1';
export const MAX_ANCHOR_LENGTH = 2000;

/** Minimal interview shape the drafter needs (subset of SkierDnaPayload). */
export interface AnchorDraftInput {
  where: {
    regions: Array<{ name: string }>;
    dayTypes: string[];
    terrain: string[];
    feel: number; // 0.0 planted .. 1.0 playful
  };
  body?: { height?: number; weight?: number };
  currentSki?: {
    items?: Array<{ brand?: string; model?: string; length?: number }>;
    loves?: string[];
    wouldChange?: string[];
  };
  intent?: { chosenLength?: number; chosenWaist?: number };
  design?: { skiType?: string };
}

export interface AnchorVersion {
  version: number;
  /** draft | confirmed | edited — the lifecycle of THIS version. */
  status: 'draft' | 'confirmed' | 'edited';
  missionStatement: string;
  skierDescription: string;
  /** Who produced this version: 'engine-draft' | a human actor id/email. */
  author: string;
  createdAt: string;
  /** For edited versions: what the human changed (diff summary). */
  editNote?: string;
}

export interface AnchorHistory {
  versions: AnchorVersion[];
  /** Derived current state = last version (Decision 002 Q4). */
  current: AnchorVersion | null;
}

export function emptyHistory(): AnchorHistory {
  return { versions: [], current: null };
}

// ---------------------------------------------------------------------------
// Deterministic draft: payload -> proposed anchor text
// ---------------------------------------------------------------------------

const FEEL_LABELS: Array<{ max: number; label: string }> = [
  { max: 0.2, label: 'firmly planted' },
  { max: 0.4, label: 'stability-first with a taste of playfulness' },
  { max: 0.6, label: 'balanced between planted and playful' },
  { max: 0.8, label: 'playful, with stability when it matters' },
  { max: 1.01, label: 'fully playful' },
];

function feelLabel(feel: number): string {
  const hit = FEEL_LABELS.find((f) => feel < f.max) ?? FEEL_LABELS[FEEL_LABELS.length - 1];
  return hit.label;
}

function listOr(items: string[], empty: string): string {
  return items.length ? items.join(', ') : empty;
}

/**
 * Pure draft from the interview payload. Same payload -> byte-identical text.
 * The engine proposes; the human decides.
 */
export function draftAnchors(input: AnchorDraftInput): AnchorsSection {
  const regions = listOr(
    input.where.regions.map((r) => r.name),
    'their home mountains',
  );
  const dayTypes = listOr(input.where.dayTypes, 'mixed days');
  const terrain = listOr(input.where.terrain, 'varied terrain');
  const feel = feelLabel(input.where.feel);
  const skiType = input.design?.skiType ?? 'all-mountain';

  const mission =
    `Design a ${skiType} ski for ${dayTypes} skiing on ${terrain} in ${regions}. ` +
    `The ski should feel ${feel}.` +
    (input.intent?.chosenLength ? ` Skier is targeting roughly ${input.intent.chosenLength} cm.` : '') +
    (input.intent?.chosenWaist ? ` Waist near ${input.intent.chosenWaist} mm if the design supports it.` : '');

  const cur = input.currentSki;
  const curDesc = cur?.items?.length
    ? ` They currently ski ${cur.items
        .map((i) => [i.brand, i.model, i.length ? `${i.length} cm` : null].filter(Boolean).join(' '))
        .filter(Boolean)
        .join(' and ')}.`
    : '';
  const loves = cur?.loves?.length ? ` What works: ${listOr(cur.loves, '')}.` : '';
  const change = cur?.wouldChange?.length ? ` What they would change: ${listOr(cur.wouldChange, '')}.` : '';
  const bodyBit =
    input.body?.height && input.body?.weight
      ? ` Skier: ${Math.round(input.body.height)} cm, ${Math.round(input.body.weight / 1000)} kg.`
      : '';

  const skierDescription =
    `Skis ${dayTypes} on ${terrain} in ${regions}; wants a ski that feels ${feel}.${bodyBit}${curDesc}${loves}${change}`;

  const clamp = (s: string) => (s.length > MAX_ANCHOR_LENGTH ? s.slice(0, MAX_ANCHOR_LENGTH) : s);
  return { missionStatement: clamp(mission), skierDescription: clamp(skierDescription) };
}

// ---------------------------------------------------------------------------
// Version lifecycle: draft -> confirmed | edited (append-only)
// ---------------------------------------------------------------------------

function validateText(s: string, field: string): void {
  if (s.length > MAX_ANCHOR_LENGTH) {
    throw new Error(`${field} exceeds ${MAX_ANCHOR_LENGTH} chars (${s.length})`);
  }
}

/** Human confirms the draft as-is. */
export function confirmAnchors(history: AnchorHistory, draft: AnchorsSection, actor: string, at: string): AnchorHistory {
  validateText(draft.missionStatement, 'missionStatement');
  validateText(draft.skierDescription, 'skierDescription');
  const v: AnchorVersion = {
    version: history.versions.length + 1,
    status: 'confirmed',
    missionStatement: draft.missionStatement,
    skierDescription: draft.skierDescription,
    author: actor,
    createdAt: at,
  };
  return { versions: [...history.versions, v], current: v };
}

/** Human edits before confirming — the edited text becomes the new version. */
export function editAnchors(
  history: AnchorHistory,
  draft: AnchorsSection,
  edited: AnchorsSection,
  actor: string,
  at: string,
): AnchorHistory {
  validateText(edited.missionStatement, 'missionStatement');
  validateText(edited.skierDescription, 'skierDescription');
  const changed: string[] = [];
  if (edited.missionStatement !== draft.missionStatement) changed.push('missionStatement');
  if (edited.skierDescription !== draft.skierDescription) changed.push('skierDescription');
  const v: AnchorVersion = {
    version: history.versions.length + 1,
    status: 'edited',
    missionStatement: edited.missionStatement,
    skierDescription: edited.skierDescription,
    author: actor,
    createdAt: at,
    editNote: changed.length ? `human edit: ${changed.join(' + ')} changed from the engine draft` : 'human re-confirmed without text change',
  };
  return { versions: [...history.versions, v], current: v };
}
